import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const LINE_REVEAL_MS = 80;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const logoStyle = {
  fontFeatureSettings: `'ss01' on`,
  fontVariantLigatures: 'no-contextual',
} as const;

const getLetterMarker = (index: number) => {
  const place = index % ALPHABET.length;
  const cycle = Math.floor(index / ALPHABET.length);
  if (cycle === 0) return ALPHABET[place];
  return `${ALPHABET[cycle - 1]}${ALPHABET[place]}`;
};

export const getNavLines = (tick: number, lineCount: number) => {
  if (lineCount <= 0) return [];
  if (tick < lineCount) {
    return Array.from({ length: tick + 1 }, (_, index) => String(index + 1));
  }
  const cycleTick = tick - lineCount;
  const replaced = (cycleTick % lineCount) + 1;
  const isLetterPass = Math.floor(cycleTick / lineCount) % 2 === 0;
  return Array.from({ length: lineCount }, (_, index) => {
    const isLetter = isLetterPass ? index < replaced : index >= replaced;
    return isLetter ? getLetterMarker(index) : String(index + 1);
  });
};

// Kept across a remount in the same turn. Cleared once the loader stays gone,
// so the next visit starts at 1.
let carriedNavTick = 0;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

export const resetCarriedNavTick = () => {
  carriedNavTick = 0;
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }
};

const useCarriedNavTick = (delay: number) => {
  const [tick, setTick] = useState(carriedNavTick);
  useEffect(() => {
    if (resetTimer) {
      clearTimeout(resetTimer);
      resetTimer = undefined;
    }
    const interval = setInterval(() => {
      setTick((current) => {
        const next = current + 1;
        carriedNavTick = next;
        return next;
      });
    }, delay);
    return () => {
      clearInterval(interval);
      resetTimer = setTimeout(() => {
        carriedNavTick = 0;
        resetTimer = undefined;
      }, 0);
    };
  }, [delay]);
  return tick;
};

const NavDigitSequence = ({ lineCount }: { lineCount: number }) => {
  const tick = useCarriedNavTick(LINE_REVEAL_MS);
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const lines = reduceMotion
    ? getNavLines(lineCount - 1, lineCount)
    : getNavLines(tick, lineCount);
  return (
    <div className="flex flex-col">
      {lines.map((line, index) => (
        <div
          key={getLetterMarker(index)}
          className="flex h-[1lh] shrink-0 items-center"
        >
          {line}
        </div>
      ))}
    </div>
  );
};

const NavDigitTicker = () => {
  const navRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState(0);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    // ResizeObserver tracks the nav so the digit count fills its height.
    const measure = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(nav).lineHeight);
      if (!lineHeight) return;
      const next = Math.floor(nav.clientHeight / lineHeight);
      if (next > 0) setLineCount(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={navRef}
      aria-hidden
      data-testid="nav-digit-ticker"
      className="relative min-h-0 flex-1 overflow-hidden text-main-500"
    >
      {lineCount > 0 && <NavDigitSequence lineCount={lineCount} />}
      <div
        data-testid="nav-digit-fade"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-canvas"
        style={{
          backdropFilter: 'blur(12px)',
          maskImage: 'linear-gradient(to bottom, transparent, black)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black)',
        }}
      />
    </div>
  );
};

export const TopLevelLoader = () => (
  <div
    role="status"
    aria-label="Loading"
    data-testid="top-level-loader"
    className="relative flex h-screen flex-col overflow-x-hidden bg-canvas text-xmd"
  >
    <header className="relative z-20 flex shrink-0 flex-row items-center gap-f1 border-b-[0.5px] border-main-300 bg-canvas p-[2ch]">
      <span style={logoStyle}>FRAGMENT</span>
    </header>
    <div className="relative z-0 flex min-h-0 flex-1 flex-col">
      <div className="flex h-full flex-row overflow-auto px-f1 laptop:px-f2">
        <div
          className={clsx(
            'fixed left-f0 laptop:absolute',
            'z-20 flex h-full w-f23 min-w-f23 max-w-f23 flex-col bg-canvas',
            'overflow-y-auto scrollbar-hide border-r-[0.5px] border-main-300',
            'pb-f6 pl-f2 pr-f2 pt-f2'
          )}
        >
          <NavDigitTicker />
        </div>
        <main className="h-full w-full laptop:grow laptop:pl-f21" />
      </div>
    </div>
    <div
      className={clsx(
        'absolute bottom-0 left-0 z-30 bg-canvas',
        'flex h-f6 w-f23 flex-row items-center',
        'border-r-[0.5px] border-main-300 px-f2'
      )}
      data-testid="loading-account"
    >
      <div
        aria-hidden
        data-testid="loading-avatar"
        className="flex h-f2 w-f2 min-w-f2 items-center justify-center rounded-full border-[0.5px] border-main-300 bg-canvas"
      />
    </div>
  </div>
);
